


import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import {TMeeting} from '@/_types/meeting/meeting.type';
import DateTimeHelper from '@/_helpers/date-time.helper';
import {TEvent} from '@/_types/event.type';
import {TContact} from '@/_types/contact.type';
import {Action, Getter} from 'vuex-class';
import _cloneDeep from 'lodash.clonedeep';
import {TSimpleTimeSlot} from '@/_modules/standalone-company/types/standalone-company-store-state.type';
import {DateTimeFormat} from '@/_types/date-time-format.enum';
import {MeetingStatus} from '@/_modules/meeting-rooms/types/meeting-status.enum';

@Component
export default class TimePicker extends Vue {

  @Getter('meetingsStore/getMeetingsByUserId') public readonly meetingsByUserId: (userId: number) => TMeeting[];
  @Action('meetingsStore/requestUserMeetings') requestUserMeetings: (params: { userId: number; force?: boolean }) => Promise<TMeeting[]>;
  @Action('standaloneCompanyStore/setTimeSlotSelection') setTimeSlotSelection: (params: TSimpleTimeSlot[]) => void;
  @Getter('standaloneCompanyStore/getTimeSlotSelection') public readonly getTimeSlotSelection: TSimpleTimeSlot[];

  public timeSlots: TSimpleTimeSlot[] = [];
  public timeSlotSelection: TSimpleTimeSlot[] = [];
  public nowTimestamp: number = (new Date()).getTime();
  public nowTimestampUpdaterIntervalId: number = null;

  @Prop({type: Date, default: null})
  public readonly selectedDay: Date;

  @Prop({type: Object, default: null})
  public readonly event: TEvent;

  @Prop()
  public readonly chosenTeamMember: TContact;

  public get chosenTeamMemberUserId(): number {
    if (!this.chosenTeamMember) {
      return null;
    }
    return this.chosenTeamMember.user_id;
  }

  @Watch('selectedDay', {immediate: true})
  private onSelectedDayChange(): void {
    this.createTimeSlots();
  }

  @Watch('chosenTeamMemberUserId', {immediate: true})
  private onChosenTeamMemberUserIdChange(): void {
    if (!this.chosenTeamMemberUserId) {
      return;
    }
    this.requestUserMeetings({
      userId: this.chosenTeamMemberUserId,
      force: true,
    });
  }

  public get timeSlotButtonsHeading(): string {
    // TODO: complete with all translations
    if (this.selectedDay) {
      const localeName: string = this.$i18n.locale;
      let formatString: string = DateTimeFormat.DAY_NAME_DATE_MONTH_EN;
      if (localeName === 'uk' || localeName === 'ru') {
        formatString = DateTimeFormat.DAY_NAME_DATE_MONTH_UK;
      }
      return this.$moment(this.selectedDay).format(formatString);
    }
    return this.$t('standaloneCompany.chooseDayFirst') as string;
  }

  public get isTimePickerDisabled(): boolean {
    return !this.selectedDay;
  }

  public get meetings(): TMeeting[] {
    if (!this.chosenTeamMemberUserId) {
      return [];
    }
    return this.meetingsByUserId(this.chosenTeamMemberUserId);
  }

  public get timeSlotsPastTimeMark(): number {
    // Current time - 30 minutes, as specified in AW-1779
    // See isTimeSlotPast for comparison
    return this.nowTimestamp - (1000 * 60 * 30);
  }

  public set timeSlotsPastTimeMark(value: number) {
    this.nowTimestamp = value;
  }

  public mounted(): void {
    this.startNowTimestampUpdate();
  }

  public beforeDestroy(): void {
    this.stopNowTimestampUpdate();
  }

  private startNowTimestampUpdate(): void {
    this.nowTimestampUpdaterIntervalId = window.setInterval(() => {
      this.timeSlotsPastTimeMark = (new Date()).getTime();
    }, 1000 * 60);
  }

  private stopNowTimestampUpdate(): void {
    window.clearInterval(this.nowTimestampUpdaterIntervalId);
  }

  private createTimeSlots(): void {
    this.timeSlots = [];
    let startSlotsFrom: Date = this.selectedDay;

    if (!this.selectedDay) {
      startSlotsFrom = new Date();
    }

    startSlotsFrom.setHours(0);
    startSlotsFrom.setMinutes(0);
    startSlotsFrom.setSeconds(0);
    startSlotsFrom.setMilliseconds(0);

    const selectedDateMoment = this.$moment(startSlotsFrom);
    let iterateTimeMoment = selectedDateMoment.clone()
      .hours(0)
      .minutes(0)
      .seconds(0)
      .milliseconds(0);

    const hours: number[] = [10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];

    hours.forEach(h => {
      iterateTimeMoment.hours(h).minutes(0);
      const nextTimeMoment = iterateTimeMoment.clone().add(30, 'minutes');

      this.timeSlots.push({
        dateStart: iterateTimeMoment.toDate(),
        dateEnd: nextTimeMoment.toDate(),
      });

      if (h !== hours[hours.length - 1]) {
        this.timeSlots.push({
          dateStart: iterateTimeMoment.minutes(30).toDate(),
          dateEnd: nextTimeMoment.hours(h + 1).minutes(0).toDate(),
        });
      }

      iterateTimeMoment = nextTimeMoment;
    });
  }

  private getTimeSlotTime(date: Date): string {
    return DateTimeHelper.getHoursMinutes(date);
  }

  private get myMeetingsConfirmedAndUnconfirmed(): TMeeting[] {
    return this.meetings.filter(meeting => {
      return (
        meeting.is_creator === true
        && (meeting.status === 'confirmed' || meeting.status === 'unconfirmed')
      );
    });
  }

  private onTimeSlotClick(timeSlot: TSimpleTimeSlot): void {
    if (this.isTimeSlotSelected(timeSlot)) {
      const timestampStart = timeSlot.dateStart.getTime();
      this.timeSlotSelection = this.timeSlotSelection.filter(item => {
        return item.dateStart.getTime() !== timestampStart;
      });
      this.setTimeSlotSelection(_cloneDeep(this.timeSlotSelection));
      return;
    }
    if (this.timeSlotSelection.length + this.myMeetingsConfirmedAndUnconfirmed.length === 3) {
      return;
    }
    this.timeSlotSelection.push(_cloneDeep(timeSlot));

    this.setTimeSlotSelection(_cloneDeep(this.timeSlotSelection));
  }

  private isTimeSlotSelected(timeSlot: TSimpleTimeSlot): boolean {
    const timestampStart = timeSlot.dateStart.getTime();
    const isInSelection = !!this.timeSlotSelection.find(item => {
      return (item.dateStart.getTime() === timestampStart);
    });
    return this.isTimeSlotHavingMyUnconfirmedOrConfirmedMeeting(timeSlot)
      || isInSelection;
  }

  private isTimeSlotOutsideEventRange(timeSlot: TSimpleTimeSlot): boolean {
    const tsStartTimestamp: number = timeSlot.dateStart.getTime();
    const tsEndTimestamp: number = timeSlot.dateEnd.getTime();

    if (!this.event) {
      return true;
    }

    const eventStartTimestamp: number = this.event.date_start.getTime();
    const eventEndTimestamp: number = this.event.date_end.getTime();

    return (
      tsStartTimestamp < eventStartTimestamp
      || tsEndTimestamp > eventEndTimestamp
    );
  }

  private isTimeSlotPast(timeSlot: TSimpleTimeSlot): boolean {
    return timeSlot.dateEnd.getTime() < this.timeSlotsPastTimeMark;
  }

  private findAllMeetingsForTimeSlot(timeSlot: TSimpleTimeSlot): TMeeting[] {
    return this.meetings.filter(meeting => {
      return meeting.date_start.getTime() === timeSlot.dateStart.getTime();
    });
  }

  private isTimeSlotMarkedNotAvailableByOwner(timeSlot: TSimpleTimeSlot): boolean {
    const slotMeetings: TMeeting[] = this.findAllMeetingsForTimeSlot(timeSlot);

    if (!slotMeetings || !slotMeetings.length) {
      return false;
    }

    return !!slotMeetings.find(meeting => {
      return (meeting.status === 'confirmed'
        && meeting.creator_contact.user_id === this.chosenTeamMemberUserId
        && meeting.user_contact.user_id === this.chosenTeamMemberUserId
        && meeting.contact.user_id === this.chosenTeamMemberUserId);
    });

  }

  private isTimeSlotHavingMyUnconfirmedOrConfirmedMeeting(timeSlot: TSimpleTimeSlot): boolean {
    const slotMeetings: TMeeting[] = this.findAllMeetingsForTimeSlot(timeSlot);

    if (!slotMeetings || !slotMeetings.length) {
      return false;
    }

    return !!slotMeetings.find(meeting => {
      return (
        (meeting.status === MeetingStatus.Unconfirmed
        || meeting.status === MeetingStatus.Confirmed)
        && meeting.is_creator === true
      );
    });
  }
}
